import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  searchInventory,
  searchBills,
  getBill,
  getInventoryItem,
  type InventoryItem,
  type Bill,
  type BillDetail,
} from "@/lib/inventory";
import { searchTickets, getTicket, type DeskTicketSummary } from "@/lib/mint";
import { normalizeGender } from "@/lib/passportName";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  MintPassportForm,
  type MintPrefill,
} from "@/components/dpp/MintPassportForm";
import { GuideModal } from "@/components/help/GuideModal";
import { GUIDE_ENABLED } from "@/lib/env";

type Tab = "items" | "bills" | "tickets" | "manual";

const TAB_LABELS: Record<Tab, string> = {
  items: "Zoho item",
  bills: "Zoho bill",
  tickets: "Desk ticket",
  manual: "Manual",
};

const TAB_HINTS: Record<Tab, string> = {
  items: "Search Zoho Inventory items and pick a serial to mint.",
  bills: "Find a bill, then pick the line item you are minting.",
  tickets: "Find a Desk ticket to mint from, with its photos attached.",
  manual: "Enter every passport detail by hand, with no external source.",
};

const monoMeta: React.CSSProperties = {
  fontFamily: "var(--f-mono)",
  fontSize: 10.5,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: "var(--ink-mute)",
};

const ITEM_SPEC_FIELDS: { cf: string; type: string }[] = [
  { cf: "cf_dial_color", type: "Dial Color" },
  { cf: "cf_case_material", type: "Case Material" },
  { cf: "cf_band_material", type: "Bracelet Material" },
];

function attributesFromItem(item: InventoryItem) {
  return ITEM_SPEC_FIELDS.map((f) => ({
    type: f.type,
    value: (item.cfh?.[f.cf] ?? item.cf?.[f.cf] ?? "").trim(),
  })).filter((a) => a.value);
}

function genderFromItem(item: InventoryItem): string {
  return normalizeGender(item.cfh?.cf_gender ?? item.cf?.cf_gender ?? "");
}

function itemTitle(item: {
  brand: string;
  model: string;
  name: string;
}): string {
  return (
    [item.brand, item.model].filter(Boolean).join(" · ") || item.name || "—"
  );
}

export function MintPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("tickets");
  const [prefill, setPrefill] = useState<MintPrefill | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [minted, setMinted] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  function selectSource(next: MintPrefill) {
    setPrefill(next);
    setMinted(false);
    setFormKey((n) => n + 1);
  }

  function switchTab(next: Tab) {
    setTab(next);
    setPrefill(null);
    setMinted(false);
    setFormKey((n) => n + 1);
  }

  function mintAnother() {
    setPrefill(null);
    setMinted(false);
    setFormKey((n) => n + 1);
  }

  const showForm = tab === "manual" || prefill !== null;

  return (
    <div className="adm__body">
      <div className="adm__page-head">
        <div className="adm__title-stack">
          <button
            type="button"
            onClick={() => navigate({ to: "/dpps" })}
            style={{
              ...monoMeta,
              background: "transparent",
              border: 0,
              cursor: "pointer",
              padding: 0,
              textAlign: "left",
            }}
          >
            ‹ Back to passports
          </button>
          <h1 className="h1" style={{ fontSize: 36, margin: 0 }}>
            Mint passport
          </h1>
        </div>
        {GUIDE_ENABLED && (
          <Button size="sm" variant="ghost" onClick={() => setGuideOpen(true)}>
            How minting works
          </Button>
        )}
      </div>

      {GUIDE_ENABLED && (
        <GuideModal
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
          topic="mint"
        />
      )}

      <div
        style={{
          display: "flex",
          gap: 4,
          borderBottom: "1px solid var(--line)",
          marginBottom: 20,
        }}
      >
        {(["tickets", "bills", "items", "manual"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => switchTab(t)}
            style={{
              background: "transparent",
              border: 0,
              borderBottom: `2px solid ${
                tab === t ? "var(--accent)" : "transparent"
              }`,
              color: tab === t ? "var(--ink)" : "var(--ink-mute)",
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: "pointer",
              padding: "10px 14px",
              marginBottom: -1,
            }}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 760 }}>
        {!showForm && (
          <div style={{ ...monoMeta, marginBottom: 16, textTransform: "none" }}>
            {TAB_HINTS[tab]}
          </div>
        )}

        {showForm ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {tab !== "manual" && !minted && (
              <button
                type="button"
                onClick={() => setPrefill(null)}
                style={{
                  ...monoMeta,
                  alignSelf: "flex-start",
                  background: "transparent",
                  border: 0,
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                ‹ Pick a different source
              </button>
            )}
            <MintPassportForm
              key={formKey}
              prefill={prefill ?? undefined}
              manualEntry={tab === "manual"}
              onMinted={() => setMinted(true)}
            />
            {minted && (
              <div style={{ display: "flex", gap: 12 }}>
                <Button size="sm" onClick={mintAnother}>
                  Mint another
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate({ to: "/dpps" })}
                >
                  Back to passports
                </Button>
              </div>
            )}
          </div>
        ) : tab === "items" ? (
          <ItemsSearch onSelect={selectSource} />
        ) : tab === "bills" ? (
          <BillsSearch onSelect={selectSource} />
        ) : (
          <TicketsSearch onSelect={selectSource} />
        )}
      </div>
    </div>
  );
}

function ItemsSearch({ onSelect }: { onSelect: (p: MintPrefill) => void }) {
  const [nameQuery, setNameQuery] = useState("");
  const [skuQuery, setSkuQuery] = useState("");
  const [debouncedName, setDebouncedName] = useState("");
  const [debouncedSku, setDebouncedSku] = useState("");
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedName(nameQuery.trim());
      setDebouncedSku(skuQuery.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [nameQuery, skuQuery]);

  const activeName = debouncedName.length >= 2 ? debouncedName : "";
  const activeSku = debouncedSku.length >= 2 ? debouncedSku : "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    searchInventory({
      name: activeName || undefined,
      sku: activeSku || undefined,
    })
      .then((res) => {
        if (!cancelled) setItems(res);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to search inventory");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeName, activeSku]);

  async function openItem(item: InventoryItem) {
    setOpening(true);
    setError("");
    try {
      const detail = await getInventoryItem(item.itemId);
      onSelect({
        brand: detail.item.brand,
        model: detail.item.model,
        reference: detail.item.reference,
        gender: genderFromItem(detail.item),
        customAttributes: attributesFromItem(detail.item),
        itemId: detail.item.itemId,
        serialOptions: detail.serials.map((s) => ({
          serialNumber: s.serialNumber,
          minted: s.minted,
        })),
        billNumber:
          detail.serials.find((s) => !s.minted && s.billNumber)?.billNumber ||
          undefined,
        sourceLabel: `Zoho item ${itemTitle(detail.item)}`,
      });
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to load item"));
    } finally {
      setOpening(false);
    }
  }

  if (opening) {
    return (
      <div
        style={{ ...monoMeta, display: "flex", gap: 8, alignItems: "center" }}
      >
        <Spinner /> Loading item…
      </div>
    );
  }

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className="field">
          <Label htmlFor="mint-item-name">Item name</Label>
          <Input
            id="mint-item-name"
            placeholder="Brand, model, reference…"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <Label htmlFor="mint-item-sku">SKU</Label>
          <Input
            id="mint-item-sku"
            placeholder="SKU"
            value={skuQuery}
            onChange={(e) => setSkuQuery(e.target.value)}
          />
        </div>
      </div>

      <ResultsStatus
        loading={loading}
        error={error}
        count={items.length}
        emptyLabel="No items found"
      />

      {!loading && items.length > 0 && (
        <ResultsList>
          {items.map((it) => (
            <ResultRow
              key={it.itemId}
              title={itemTitle(it)}
              subtitle={[
                it.reference ? `REF ${it.reference}` : "",
                it.sku ? `SKU ${it.sku}` : "",
              ]
                .filter(Boolean)
                .join(" · ")}
              aside={`${it.stock} in stock`}
              onClick={() => void openItem(it)}
            />
          ))}
        </ResultsList>
      )}
    </>
  );
}

function BillsSearch({ onSelect }: { onSelect: (p: MintPrefill) => void }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [detail, setDetail] = useState<BillDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const active = debounced.length >= 2 ? debounced : "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    searchBills(active || undefined)
      .then((res) => {
        if (!cancelled) setBills(res);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to search bills");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  function openBill(bill: Bill) {
    setDetailLoading(true);
    setDetailError("");
    setDetail(null);
    getBill(bill.billId)
      .then((d) => {
        if (d.items.length === 1) {
          selectBillItem(d, 0);
        } else {
          setDetail(d);
        }
      })
      .catch(() => setDetailError("Failed to load bill"))
      .finally(() => setDetailLoading(false));
  }

  function selectBillItem(d: BillDetail, index: number) {
    const line = d.items[index];
    if (!line) return;
    onSelect({
      brand: line.item.brand,
      model: line.item.model,
      reference: line.item.reference,
      gender: genderFromItem(line.item),
      customAttributes: attributesFromItem(line.item),
      itemId: line.item.itemId,
      serialOptions: line.serials.map((s) => ({
        serialNumber: s.serialNumber,
        minted: s.minted,
      })),
      billNumber: d.bill.billNumber,
      sourceLabel: `bill ${d.bill.billNumber} · ${itemTitle(line.item)}`,
    });
  }

  if (detailLoading) {
    return (
      <div
        style={{ ...monoMeta, display: "flex", gap: 8, alignItems: "center" }}
      >
        <Spinner /> Loading bill…
      </div>
    );
  }

  if (detail) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <button
          type="button"
          onClick={() => setDetail(null)}
          style={{
            ...monoMeta,
            alignSelf: "flex-start",
            background: "transparent",
            border: 0,
            cursor: "pointer",
            padding: 0,
          }}
        >
          ‹ Back to bills
        </button>
        <div style={monoMeta}>
          Bill {detail.bill.billNumber} · select an item
        </div>
        {detail.items.length === 0 ? (
          <div style={{ ...monoMeta, textTransform: "none" }}>
            No serial-tracked items on this bill.
          </div>
        ) : (
          <ResultsList>
            {detail.items.map((line, i) => (
              <ResultRow
                key={line.item.itemId}
                title={itemTitle(line.item)}
                subtitle={[
                  line.item.reference ? `REF ${line.item.reference}` : "",
                  `${line.serials.length} serial${
                    line.serials.length === 1 ? "" : "s"
                  }`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
                onClick={() => selectBillItem(detail, i)}
              />
            ))}
          </ResultsList>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="field">
        <Label htmlFor="mint-bill">Bill number</Label>
        <Input
          id="mint-bill"
          placeholder="Bill number…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      <ResultsStatus
        loading={loading}
        error={error || detailError}
        count={bills.length}
        emptyLabel="No bills found"
      />

      {!loading && bills.length > 0 && (
        <ResultsList>
          {bills.map((b) => (
            <ResultRow
              key={b.billId}
              title={b.billNumber || "—"}
              subtitle={[b.vendorName, b.date].filter(Boolean).join(" · ")}
              aside={b.status}
              onClick={() => openBill(b)}
            />
          ))}
        </ResultsList>
      )}
    </>
  );
}

function TicketsSearch({ onSelect }: { onSelect: (p: MintPrefill) => void }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [tickets, setTickets] = useState<DeskTicketSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const active = debounced.length >= 2 ? debounced : "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    searchTickets(active || undefined)
      .then((res) => {
        if (!cancelled) setTickets(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(apiErrorMessage(err, "Failed to load tickets"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  async function openTicket(ticket: DeskTicketSummary) {
    setOpening(true);
    setError("");
    try {
      const detail = await getTicket(ticket.id);
      onSelect({
        ...prefillFromTicketFields(detail.ticket.cf),
        billNumber: detail.billNumber ?? undefined,
        attachments: detail.attachments,
        sourceLabel: `Desk ticket #${
          detail.ticket.ticketNumber || detail.ticket.id
        } · ${detail.ticket.subject}`,
      });
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to load ticket"));
    } finally {
      setOpening(false);
    }
  }

  if (opening) {
    return (
      <div
        style={{ ...monoMeta, display: "flex", gap: 8, alignItems: "center" }}
      >
        <Spinner /> Loading ticket…
      </div>
    );
  }

  return (
    <>
      <div className="field">
        <Label htmlFor="mint-ticket">Ticket</Label>
        <Input
          id="mint-ticket"
          placeholder="Subject, ticket number, serial…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
      </div>

      <ResultsStatus
        loading={loading}
        error={error}
        count={tickets.length}
        emptyLabel="No tickets found"
      />

      {!loading && tickets.length > 0 && (
        <ResultsList>
          {tickets.map((t) => (
            <ResultRow
              key={t.id}
              title={t.subject || "—"}
              subtitle={[
                t.ticketNumber ? `#${t.ticketNumber}` : "",
                t.contactName,
                t.createdTime ? t.createdTime.slice(0, 10) : "",
              ]
                .filter(Boolean)
                .join(" · ")}
              aside={t.status}
              onClick={() => void openTicket(t)}
            />
          ))}
        </ResultsList>
      )}
    </>
  );
}

const TICKET_FIELD_MATCHERS: {
  field: "brand" | "model" | "serialNumber" | "reference";
  match: (key: string) => boolean;
}[] = [
  { field: "brand", match: (k) => k.includes("brand") || k.includes("make") },
  { field: "model", match: (k) => k.includes("model") },
  {
    field: "serialNumber",
    match: (k) => k.includes("serial") || k.includes("case_number"),
  },
  {
    field: "reference",
    match: (k) => k.includes("reference") || k.endsWith("_ref"),
  },
];

const TICKET_ATTRIBUTE_MATCHERS: {
  type: string;
  match: (k: string) => boolean;
}[] = [
  { type: "Dial Color", match: (k) => k.includes("dial") },
  { type: "Case Material", match: (k) => k.includes("case_material") },
  {
    type: "Bracelet Material",
    match: (k) =>
      k.includes("bracelet") || k.includes("band") || k.includes("strap"),
  },
];

export function prefillFromTicketFields(
  cf: Record<string, string>
): MintPrefill {
  const prefill: MintPrefill = {};
  const claimed = new Set<string>();

  for (const { field, match } of TICKET_FIELD_MATCHERS) {
    const key = Object.keys(cf).find((k) => !claimed.has(k) && match(norm(k)));
    if (key) {
      prefill[field] = cf[key];
      claimed.add(key);
    }
  }

  const genderKey = Object.keys(cf).find(
    (k) => !claimed.has(k) && norm(k).includes("gender")
  );
  if (genderKey) {
    prefill.gender = normalizeGender(cf[genderKey]);
    claimed.add(genderKey);
  }

  const attributes: { type: string; value: string }[] = [];
  for (const { type, match } of TICKET_ATTRIBUTE_MATCHERS) {
    const key = Object.keys(cf).find((k) => !claimed.has(k) && match(norm(k)));
    if (key) {
      attributes.push({ type, value: cf[key] });
      claimed.add(key);
    }
  }
  if (attributes.length > 0) prefill.customAttributes = attributes;

  return prefill;
}

function norm(key: string): string {
  return key.toLowerCase().replace(/^cf_/, "");
}

function ResultsStatus({
  loading,
  error,
  count,
  emptyLabel,
}: {
  loading: boolean;
  error: string;
  count: number;
  emptyLabel: string;
}) {
  return (
    <div
      style={{
        ...monoMeta,
        display: "flex",
        gap: 8,
        alignItems: "center",
        margin: "12px 0",
      }}
    >
      {loading ? (
        <>
          <Spinner /> Searching…
        </>
      ) : error ? (
        <span style={{ color: "var(--danger)", textTransform: "none" }}>
          {error}
        </span>
      ) : count === 0 ? (
        emptyLabel
      ) : (
        <>
          <b style={{ color: "var(--ink)" }}>{count}</b> results
        </>
      )}
    </div>
  );
}

function ResultsList({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: 420,
        overflowY: "auto",
        border: "1px solid var(--line)",
      }}
    >
      {children}
    </div>
  );
}

function ResultRow({
  title,
  subtitle,
  aside,
  onClick,
}: {
  title: string;
  subtitle?: string;
  aside?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 12px",
        background: "transparent",
        border: 0,
        borderBottom: "1px solid var(--line)",
        textAlign: "left",
        cursor: "pointer",
        width: "100%",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--surface-3)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, color: "var(--ink)" }}>{title}</div>
        {subtitle && (
          <div style={{ ...monoMeta, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      {aside && (
        <span style={{ ...monoMeta, whiteSpace: "nowrap" }}>{aside}</span>
      )}
      <span style={{ color: "var(--ink-mute)" }}>›</span>
    </button>
  );
}
